import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Truck, UserPlus } from 'lucide-react';
import AdminConversationWorkspace from './AdminConversationWorkspace';
import ConversationList from './ConversationList';
import DriverTimelineDrawer from './DriverTimelineDrawer';
import ProspectLeadsDrawer from './ProspectLeadsDrawer';
import { fetchProspectSummary } from './api';
import { socket } from '../../admin/chat/socket';

const ADMIN_FILTERS = [
  ['all', 'Todos'],
  ['unread', 'Sin leer'],
  ['pending_draft', 'AI drafts'],
  ['driver_issue', 'Driver issue'],
  ['customer_not_answering', 'Cliente no contesta'],
  ['ai_handoff', 'Handoff AI'],
  ['unresolved_identity', 'Sin identidad'],
  ['cod_payment', 'COD/payment'],
  ['whatsapp', 'WhatsApp'],
  ['support_general', 'Fast Max'],
  ['store', 'Clientes'],
  ['provider', 'Proveedores'],
  ['driver', 'Motorizados'],
  ['delivery_issue', 'Incidencias'],
];

export default function AdminCommunicationInbox({ userId, setActiveComponent }) {
  const [selected, setSelected] = useState(null);
  const [driverTimelineOpen, setDriverTimelineOpen] = useState(false);
  const [prospectDrawerOpen, setProspectDrawerOpen] = useState(false);
  const [prospectSummary, setProspectSummary] = useState(null);

  const loadProspectSummary = useCallback(async () => {
    try {
      const data = await fetchProspectSummary({ days: 7 });
      setProspectSummary(data);
    } catch (error) {
      console.error('Prospect summary error:', error);
    }
  }, []);

  useEffect(() => {
    loadProspectSummary();
  }, [loadProspectSummary]);

  useEffect(() => {
    const handleLeadUpdate = (payload) => {
      if (payload?.summary) {
        setProspectSummary(payload.summary);
      } else {
        loadProspectSummary();
      }
    };
    socket.on('communication_lead_updated', handleLeadUpdate);
    return () => socket.off('communication_lead_updated', handleLeadUpdate);
  }, [loadProspectSummary]);

  const remainingProspects = prospectSummary?.remaining || 0;

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-slate-100">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <button
          onClick={() => setActiveComponent?.('OptionsAdmin')}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200"
        >
          <ArrowLeft className="h-4 w-4" />
          Menú
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-extrabold text-slate-950 font-montserrat">Communication Hub</h1>
          <p className="text-xs text-slate-500">Conversaciones internas, eventos y WhatsApp por entrega</p>
        </div>
        <button
          type="button"
          onClick={() => setProspectDrawerOpen(true)}
          className="relative inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-extrabold text-emerald-700 hover:bg-emerald-100"
        >
          <UserPlus className="h-4 w-4" />
          <span className="hidden sm:inline">Nuevos clientes</span>
          <span className="sm:hidden">Leads</span>
          {remainingProspects > 0 && (
            <span className="ml-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-black text-white">
              {remainingProspects}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setDriverTimelineOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs font-extrabold text-blue-700 hover:bg-blue-100"
        >
          <Truck className="h-4 w-4" />
          <span className="hidden sm:inline">Motorizados hoy</span>
          <span className="sm:hidden">Rutas</span>
        </button>
      </div>
      <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className={`${selected ? 'hidden sm:block' : 'block'} w-full sm:w-[430px]`}>
        <ConversationList
          scope="admin"
          userId={userId}
          selectedConversationId={selected?.id}
          onSelect={setSelected}
          title="Comunicación Fast Max"
          subtitle="Hilos por entrega, eventos, WhatsApp y soporte directo"
          filterOptions={ADMIN_FILTERS}
          extraParams={{ include_triage: 1 }}
          showCommandCenter
        />
      </div>
      <div className={`${selected ? 'flex' : 'hidden sm:flex'} min-w-0 flex-1 flex-col p-4`}>
        <AdminConversationWorkspace conversation={selected} userId={userId} />
      </div>
      </div>
      <DriverTimelineDrawer
        open={driverTimelineOpen}
        userId={userId}
        role="admin"
        onClose={() => setDriverTimelineOpen(false)}
        onSelectConversation={setSelected}
      />
      <ProspectLeadsDrawer
        open={prospectDrawerOpen}
        onClose={() => setProspectDrawerOpen(false)}
        onSelectConversation={setSelected}
      />
    </div>
  );
}
